import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Locale } from "@/api/types";
import { sessionApi } from "@/features/auth/session";
import { useConfig } from "@/features/config/ConfigContext";
import {
  formatAmount,
  formatDateTime,
  formatNumber,
  translate,
  type MessageParameters,
} from "./format";
import type { MessageKey } from "./catalogs";

interface I18nContextValue {
  locale: Locale;
  timezone: string;
  savingLocale: boolean;
  localeError: string | null;
  /** `persist: false` changes the language on this device only, with no account call. */
  setLocale: (locale: Locale, options?: { persist?: boolean }) => Promise<void>;
  t: (key: MessageKey, parameters?: MessageParameters) => string;
  dateTime: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => string;
  number: (value: number) => string;
  amount: (value: number, currency: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);
export const LOCALE_STORAGE_KEY = "memoru-locale";

function browserLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === "en" || stored === "id") return stored;
  } catch {
    // Browser language remains a safe fallback when storage is unavailable.
  }
  return navigator.language.toLowerCase().startsWith("id") ? "id" : "en";
}

function storeLocale(locale: Locale) {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // The selection still applies for the current tab.
  }
}

function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const { user, updateUser } = useConfig();
  const [locale, setCurrentLocale] = useState<Locale>(browserLocale);
  const [timezone, setTimezone] = useState(() => user?.timezone ?? browserTimezone());
  const [savingLocale, setSavingLocale] = useState(false);
  const [localeError, setLocaleError] = useState<string | null>(null);
  const savedLocale = user?.locale;

  useEffect(() => {
    if (!user) return;
    setTimezone(user.timezone);
  }, [user]);

  // The locale saved on the account wins over the browser default, so the
  // language follows the user across devices.
  useEffect(() => {
    if (savedLocale !== "en" && savedLocale !== "id") return;
    setCurrentLocale(savedLocale);
    storeLocale(savedLocale);
  }, [savedLocale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(async (nextLocale: Locale, options?: { persist?: boolean }) => {
    // Switch the UI immediately; persisting to the account happens after.
    setCurrentLocale(nextLocale);
    storeLocale(nextLocale);
    setLocaleError(null);
    if (options?.persist === false || !user || user.locale === nextLocale) return;
    setSavingLocale(true);
    try {
      updateUser(await sessionApi.updateLocale(nextLocale));
    } catch (error) {
      setLocaleError(error instanceof Error ? error.message : "Could not save language");
    } finally {
      setSavingLocale(false);
    }
  }, [user, updateUser]);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    timezone,
    savingLocale,
    localeError,
    setLocale,
    t: (key, parameters) => translate(locale, key, parameters),
    dateTime: (input, options) => formatDateTime(input, locale, timezone, options),
    number: (input) => formatNumber(input, locale),
    amount: (input, currency) => formatAmount(input, currency, locale),
  }), [locale, timezone, savingLocale, localeError, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used within I18nProvider");
  return context;
}
